import React, { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import StatusPill from '../../components/shared/StatusPill';
import AreaChart from '../../components/charts/AreaChart';
import { consumerPlant, consumerPowerCurve, consumerLast15MinKwh } from '../../data/mockData';

type TimeWindow = 'live' | '15min' | '1hr' | '4hr' | 'today';

const TIME_WINDOWS: { key: TimeWindow; label: string }[] = [
  { key: 'live',  label: 'Live'        },
  { key: '15min', label: 'Last 15 min' },
  { key: '1hr',   label: 'Last 1 hr'   },
  { key: '4hr',   label: 'Last 4 hr'   },
  { key: 'today', label: 'Today'       },
];

/** Compute how many data points to show based on window (each point = 15 min) */
function windowedPoints(window: TimeWindow): typeof consumerPowerCurve {
  if (window === 'live' || window === 'today') return consumerPowerCurve;
  const count = window === '15min' ? 2 : window === '1hr' ? 5 : 17;
  return consumerPowerCurve.slice(-count);
}

/** Energy in the current window (kWh = avg kW × hours) */
function windowedKwh(window: TimeWindow): string {
  if (window === 'live')   return `${consumerPlant.livePowerKW} kW`;
  if (window === 'today')  return `${consumerPlant.todayKWh} kWh`;
  const pts = windowedPoints(window);
  const avgKW = pts.reduce((s, p) => s + (p.power as number), 0) / pts.length;
  const hours = window === '15min' ? 0.25 : window === '1hr' ? 1 : 4;
  return `${(avgKW * hours).toFixed(2)} kWh`;
}

export default function ConsumerDashboard() {
  const plant = consumerPlant;
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('today');

  const windowPillCls = (w: TimeWindow) =>
    `px-2.5 py-1 text-xs rounded-pill border transition-colors ${
      timeWindow === w
        ? 'bg-consumer-green text-white border-consumer-green'
        : 'bg-white text-text-secondary border-border hover:border-consumer-green hover:text-consumer-green'
    }`;

  const chartData = windowedPoints(timeWindow);
  const isLive    = timeWindow === 'live';

  return (
    <div className="p-6 space-y-6">
      {/* KPIs — amber theme */}
      <div className="flex gap-4">
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Live Power</p>
          <p className="text-xl font-bold text-success mt-1">{plant.livePowerKW} kW</p>
          <div className="flex items-center gap-1.5 mt-1">
            <StatusPill status="online" label="Inverter ON" />
          </div>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Today's Generation</p>
          <p className="text-xl font-bold text-text-primary mt-1">{plant.todayKWh} kWh</p>
          <p className="text-xs text-success mt-1">+8% vs yesterday</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Last 15 min</p>
          <p className="text-xl font-bold text-text-primary mt-1">{consumerLast15MinKwh} kWh</p>
          <p className="text-xs text-text-secondary mt-1">Most recent interval</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Lifetime Generation</p>
          <p className="text-xl font-bold text-text-primary mt-1">{plant.lifetimeKWh.toLocaleString()} kWh</p>
          <p className="text-xs text-text-secondary mt-1">Since Jan 2025</p>
        </div>
      </div>

      {/* Power curve + PR neighbours */}
      <div className="flex gap-4">
        {/* Power curve */}
        <div className="w-[60%] bg-white rounded-card border border-border shadow-card p-4">
          <div className="flex items-center justify-between mb-3">
            <SectionDivider title="Power Curve" subtitle={isLive ? 'Live reading' : 'Historical data'} />
            {/* Time-window selector */}
            <div className="flex items-center gap-1">
              {TIME_WINDOWS.map(tw => (
                <button key={tw.key} onClick={() => setTimeWindow(tw.key)} className={windowPillCls(tw.key)}>
                  {tw.label}
                </button>
              ))}
            </div>
          </div>

          <AreaChart
            data={chartData}
            xKey="time"
            yKey="power"
            color="#C25A0A"
            unit=" kW"
            yLabel="kW"
            yDomain={[0, 6]}
            height={220}
            tickInterval={4}
          />

          {/* Energy annotation for windowed views */}
          {!isLive && (
            <div className="mt-2 text-center">
              <span className="text-xs text-text-tertiary">
                {TIME_WINDOWS.find(t => t.key === timeWindow)?.label}:&nbsp;
                <span className="font-semibold text-text-primary">{windowedKwh(timeWindow)}</span>
                {timeWindow === '15min' && (
                  <span className="ml-2 text-text-tertiary">· Last read: {consumerLast15MinKwh} kWh</span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* PR vs neighbours */}
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="PR vs Neighbours" subtitle="Performance comparison" />
          <div className="space-y-3 mt-2">
            {plant.prNeighbours.map((nb, i) => (
              <div key={i} className="flex items-center gap-3">
                <span
                  className={`text-xs flex-shrink-0 ${nb.isMe ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}
                  style={{ width: 160 }}
                >
                  {nb.label}
                </span>
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(nb.value / 80) * 100}%`,
                      backgroundColor: nb.isMe ? '#C25A0A' : '#1A6B3A',
                      opacity: nb.isMe ? 1 : 0.5 + i * 0.15,
                    }}
                  />
                </div>
                <span
                  className={`text-xs font-medium flex-shrink-0 ${nb.isMe ? 'text-text-primary font-bold' : 'text-text-secondary'}`}
                  style={{ width: 50 }}
                >
                  {nb.value}%
                </span>
              </div>
            ))}
          </div>

          {/* Insight box */}
          <div className="mt-4 bg-green-50 border border-green-200 rounded-input p-3 flex gap-2">
            <Lightbulb size={13} className="text-consumer-green flex-shrink-0 mt-0.5" />
            <p className="text-xs text-consumer-green">
              Your plant is above pincode and district average. Keep panels clean for best results.
            </p>
          </div>
        </div>
      </div>

      {/* Secondary KPIs — 4 cards including Last 15 min */}
      <div className="flex gap-4">
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-medium uppercase tracking-wide">Performance Ratio</p>
          <p className="text-xl font-bold text-success mt-1">{plant.prPercent}%</p>
          <p className="text-xs text-success mt-1">Good zone</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-medium uppercase tracking-wide">Plant Availability</p>
          <p className="text-xl font-bold text-text-primary mt-1">{plant.plantAvailabilityPct}%</p>
          <p className="text-xs text-text-secondary mt-1">Solar hours basis · today</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-medium uppercase tracking-wide">Specific Yield</p>
          <p className="text-xl font-bold text-text-primary mt-1">{plant.specificYieldMonthly} kWh/kWp</p>
          <p className="text-xs text-text-secondary mt-1">This month</p>
        </div>
      </div>
    </div>
  );
}
